import { useState, useCallback, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import {
  ContentBlockDynamicService,
  ContentBlockDynamicContent,
} from "@/utils/api/ContentBlockDynamicService";
import { ApiError } from "@/utils/apiResultHandler";
import { useContentBlockState } from "@/contexts/ContentBlockStateContext";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseContentBlockDynamicContentReturn {
  // Data
  items: any[];
  meta: Record<string, any>;
  hasMore: boolean;
  nextCursor: string | null;

  // State
  loading: boolean;
  isLoaded: boolean;
  retryCount: number;
  nextRetryIn: number | null; // seconds until next retry, null when not retrying

  // Operations
  fetch: (
    contentBlockId: string,
    pageSize?: number,
    forceRefresh?: boolean,
  ) => Promise<OpResult>;
  fetchNext: () => Promise<OpResult>;
  reset: () => void;

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ─── Persisted state shape (stored in ContentBlockStateContext) ───────────────
//
// Keyed under a sub-namespace so it never collides with other state keys
// (e.g. imageIndex) that the consumer also stores for the same block id.

interface PersistedDynamicState {
  items: any[];
  meta: Record<string, any>;
  hasMore: boolean;
  nextCursor: string | null;
  isLoaded: boolean;
}

const DYNAMIC_STATE_KEY = "__dynamic__";

const DEFAULT_PERSISTED: PersistedDynamicState = {
  items: [],
  meta: {},
  hasMore: false,
  nextCursor: null,
  isLoaded: false,
};

// ─── Retry configuration ──────────────────────────────────────────────────────
//
// Exponential back-off delays in milliseconds. After all steps are exhausted
// the last value is reused indefinitely.

const RETRY_DELAYS_MS = [5_000, 10_000, 30_000, 60_000];

const getRetryDelay = (retryCount: number): number =>
  RETRY_DELAYS_MS[Math.min(retryCount, RETRY_DELAYS_MS.length - 1)];

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useContentBlockDynamicContent =
  (): UseContentBlockDynamicContentReturn => {
    const { getBlockState, setBlockState } = useContentBlockState();

    // ── Local UI-only state (does NOT need to survive remount) ─────────────
    // loading, lastError, retryCount, and nextRetryIn are transient — they
    // only make sense while the component is mounted.

    const [loading, setLoading] = useState(false);
    const [lastError, setLastError] = useState<ApiError | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const [nextRetryIn, setNextRetryIn] = useState<number | null>(null);

    // ── Refs — stable request context ──────────────────────────────────────

    const contentBlockIdRef = useRef<string | null>(null);
    const pageSizeRef = useRef<number | null>(null);
    const retryCountRef = useRef(0); // mirrors retryCount state without closure risk
    const retryTimerRef = useRef<NodeJS.Timeout | null>(null);
    const retryCountdownRef = useRef<NodeJS.Timeout | null>(null);
    const nextCursorRef = useRef<string | null>(null);

    // ── Persisted state helpers ─────────────────────────────────────────────

    const readPersisted = useCallback(
      (contentBlockId: string): PersistedDynamicState => {
        return (
          getBlockState(contentBlockId)[DYNAMIC_STATE_KEY] ?? DEFAULT_PERSISTED
        );
      },
      [getBlockState],
    );

    const writePersisted = useCallback(
      (
        contentBlockId: string,
        partial: Partial<PersistedDynamicState>,
      ): void => {
        const current = readPersisted(contentBlockId);
        setBlockState(contentBlockId, {
          [DYNAMIC_STATE_KEY]: { ...current, ...partial },
        });
      },
      [readPersisted, setBlockState],
    );

    // ── Trigger re-render after store writes ───────────────────────────────

    const [, setRenderTick] = useState(0);
    const bumpRender = useCallback(() => setRenderTick((t) => t + 1), []);

    // ── Retry timer cleanup ────────────────────────────────────────────────

    const clearRetryTimers = useCallback(() => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (retryCountdownRef.current) {
        clearInterval(retryCountdownRef.current);
        retryCountdownRef.current = null;
      }
      setNextRetryIn(null);
    }, []);

    // ── Helpers ────────────────────────────────────────────────────────────

    const parseJsonField = (value: string | undefined): any => {
      try {
        return value ? JSON.parse(value) : null;
      } catch {
        return null;
      }
    };

    const applyResult = useCallback(
      (
        contentBlockId: string,
        data: ContentBlockDynamicContent,
        append: boolean,
      ) => {
        const parsedItems = parseJsonField(data.items) ?? [];
        const parsedMeta = parseJsonField(data.meta) ?? {};

        const current = readPersisted(contentBlockId);
        const nextItems = append
          ? [...current.items, ...parsedItems]
          : parsedItems;

        writePersisted(contentBlockId, {
          items: nextItems,
          meta: parsedMeta,
          hasMore: data.hasMore,
          nextCursor: data.nextCursor,
          isLoaded: true,
        });

        nextCursorRef.current = data.nextCursor;

        // Clear all retry state on success
        clearRetryTimers();
        retryCountRef.current = 0;
        setRetryCount(0);
        setLastError(null);
        bumpRender();
      },
      [readPersisted, writePersisted, clearRetryTimers, bumpRender],
    );

    const clearError = useCallback(() => {
      setLastError(null);
    }, []);

    const reset = useCallback(() => {
      clearRetryTimers();

      if (contentBlockIdRef.current) {
        writePersisted(contentBlockIdRef.current, { ...DEFAULT_PERSISTED });
        nextCursorRef.current = null;
      }

      setLoading(false);
      setLastError(null);
      retryCountRef.current = 0;
      setRetryCount(0);
      contentBlockIdRef.current = null;
      pageSizeRef.current = null;
      bumpRender();
    }, [writePersisted, clearRetryTimers, bumpRender]);

    // ── Core fetch ─────────────────────────────────────────────────────────

    // Forward declaration — fetchInternal needs to reference scheduleRetry,
    // and scheduleRetry needs to reference fetchInternal. We break the cycle
    // with a stable ref.
    const fetchInternalRef = useRef<
      (
        contentBlockId: string,
        cursor: string | null,
        pageSize: number | null | undefined,
        append: boolean,
      ) => Promise<OpResult>
    >(null!);

    const scheduleRetry = useCallback(
      (contentBlockId: string, pageSize: number | null) => {
        clearRetryTimers();

        const delayMs = getRetryDelay(retryCountRef.current);
        const delaySec = Math.round(delayMs / 1000);

        retryCountRef.current += 1;
        setRetryCount(retryCountRef.current);
        setNextRetryIn(delaySec);

        // Tick the countdown down every second for UI display
        let remaining = delaySec;
        retryCountdownRef.current = setInterval(() => {
          remaining -= 1;
          setNextRetryIn(remaining > 0 ? remaining : null);
          if (remaining <= 0) {
            clearInterval(retryCountdownRef.current!);
            retryCountdownRef.current = null;
          }
        }, 1_000);

        // Schedule the actual retry
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          fetchInternalRef.current(contentBlockId, null, pageSize, false);
        }, delayMs);
      },
      [clearRetryTimers],
    );

    const fetchInternal = useCallback(
      async (
        contentBlockId: string,
        cursor: string | null,
        pageSize: number | null | undefined,
        append: boolean,
      ): Promise<OpResult> => {
        setLoading(true);
        setLastError(null);

        try {
          const result = await ContentBlockDynamicService.getDynamicContent({
            contentBlockId,
            cursor,
            pageSize: pageSize ?? undefined,
          });

          if (result.code === OP_RESULT_CODES.OK && result.hasData()) {
            applyResult(
              contentBlockId,
              result.getDataFirst() as ContentBlockDynamicContent,
              append,
            );
            return result;
          }

          const error = ApiError.fromResult(
            result,
            "Failed to fetch dynamic content",
          );
          setLastError(error);

          // Only schedule retries for the initial (non-append) fetch.
          // Prefetch failures are handled by the consumer (the component
          // clears its prefetch flag and retries on the next index advance).
          if (!append) {
            scheduleRetry(contentBlockId, pageSize ?? null);
          }

          return result;
        } catch (error) {
          const result = OpResult.fromException(error);
          const apiError = ApiError.fromException(
            error,
            "Failed to fetch dynamic content",
          );
          setLastError(apiError);

          if (!append) {
            scheduleRetry(contentBlockId, pageSize ?? null);
          }

          return result;
        } finally {
          setLoading(false);
        }
      },
      [applyResult, scheduleRetry],
    );

    // Keep the ref in sync so scheduleRetry can call fetchInternal without
    // capturing a stale closure.
    fetchInternalRef.current = fetchInternal;

    // ── Public operations ──────────────────────────────────────────────────

    /**
     * Fetch the first page of dynamic content for a content block.
     *
     * If the block has already been loaded (persisted state exists), the
     * network call is skipped and the cached items are returned immediately.
     * This means remounting the component does NOT re-fetch from the API.
     *
     * Pass `forceRefresh: true` to bypass the cache and re-fetch.
     */
    const fetch = useCallback(
      async (
        contentBlockId: string,
        pageSize?: number,
        forceRefresh = false,
      ): Promise<OpResult> => {
        contentBlockIdRef.current = contentBlockId;
        pageSizeRef.current = pageSize ?? null;

        const cached = readPersisted(contentBlockId);

        if (cached.isLoaded && !forceRefresh) {
          nextCursorRef.current = cached.nextCursor;
          // Already loaded — clear any lingering retry state from a previous
          // failed mount so the UI doesn't show a stale "retrying" message.
          clearRetryTimers();
          retryCountRef.current = 0;
          setRetryCount(0);
          bumpRender();
          return OpResult.ok(cached.items);
        }

        nextCursorRef.current = null;
        return fetchInternal(contentBlockId, null, pageSize, false);
      },
      [readPersisted, fetchInternal, clearRetryTimers, bumpRender],
    );

    /**
     * Fetch the next page using the cursor from the previous response.
     * Appends results to existing items. No-op if there are no more pages.
     */
    const fetchNext = useCallback(async (): Promise<OpResult> => {
      if (!contentBlockIdRef.current) {
        return OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "No content block loaded. Call fetch() first.",
        );
      }

      if (!nextCursorRef.current) {
        return OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "No more pages available.",
        );
      }

      return fetchInternal(
        contentBlockIdRef.current,
        nextCursorRef.current,
        pageSizeRef.current,
        true,
      );
    }, [fetchInternal]);

    // ── Return ─────────────────────────────────────────────────────────────

    const current: PersistedDynamicState = contentBlockIdRef.current
      ? readPersisted(contentBlockIdRef.current)
      : DEFAULT_PERSISTED;

    return {
      items: current.items,
      meta: current.meta,
      hasMore: current.hasMore,
      nextCursor: current.nextCursor,

      loading,
      isLoaded: current.isLoaded,
      retryCount,
      nextRetryIn,

      fetch,
      fetchNext,
      reset,

      lastError,
      clearError,
    };
  };
